import { useState } from "react";
import { mockPaymentMethods } from "../../../mocks/accountData";

const typeIcons: Record<string, string> = {
  visa: "ri-visa-line",
  mastercard: "ri-mastercard-line",
  paypal: "ri-paypal-line",
};

const typeBg: Record<string, string> = {
  visa: "bg-blue-50 border-blue-200",
  mastercard: "bg-orange-50 border-orange-200",
  paypal: "bg-sky-50 border-sky-200",
};

export default function PaymentsTab() {
  const [methods, setMethods] = useState(mockPaymentMethods);
  const [showAdd, setShowAdd] = useState(false);

  const setDefault = (id: string) => {
    setMethods((prev) =>
      prev.map((m) => ({ ...m, isDefault: m.id === id }))
    );
  };

  const removeMethod = (id: string) => {
    setMethods((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black text-cl-dark">Payment Methods</h2>
          <p className="text-gray-500 text-sm mt-0.5">Manage your saved cards and payment options</p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="bg-cl-dark text-white font-black text-sm px-5 py-2.5 rounded-xl cartoon-btn whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          Add Method
        </button>
      </div>

      {/* Add method form */}
      {showAdd && (
        <div className="bg-white rounded-2xl thick-border cartoon-shadow p-6 mb-6">
          <h3 className="font-black text-cl-dark mb-5 flex items-center gap-2">
            <i className="ri-bank-card-2-line text-cl-green"></i>
            Add New Card
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Card Number</label>
              <input
                type="text"
                placeholder="1234 5678 9012 3456"
                className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark"
              />
            </div>
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Expiry</label>
              <input
                type="text"
                placeholder="MM/YY"
                className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark"
              />
            </div>
            <div>
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">CVV</label>
              <input
                type="text"
                placeholder="···"
                className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Cardholder Name</label>
              <input
                type="text"
                placeholder="Name on card"
                className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAdd(false)}
              className="flex-1 py-3 rounded-xl border-2 border-transparent text-gray-600 font-bold text-sm cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="flex-1 bg-cl-dark text-white font-black text-sm py-3 rounded-xl cartoon-btn cursor-pointer whitespace-nowrap"
            >
              Save Card
            </button>
          </div>
        </div>
      )}

      {/* Payment methods list */}
      <div className="flex flex-col gap-4 mb-8">
        {methods.map((method) => (
          <div
            key={method.id}
            className={`bg-white rounded-2xl thick-border p-5 flex items-center justify-between gap-4 flex-wrap transition-all ${
              method.isDefault ? "ring-2 ring-cl-green" : "cartoon-shadow"
            }`}
          >
            <div className="flex items-center gap-4">
              <span className={`w-12 h-12 flex items-center justify-center rounded-xl border-2 flex-shrink-0 ${typeBg[method.type] ?? "bg-gray-100 border-transparent"}`}>
                <i className={`${typeIcons[method.type] ?? "ri-bank-card-line"} text-gray-700 text-xl`}></i>
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-black text-cl-dark text-sm">{method.label}</p>
                  {method.isDefault && (
                    <span className="bg-cl-lime text-cl-dark text-xs font-black px-2 py-0.5 rounded-full border border-cl-dark">
                      Default
                    </span>
                  )}
                </div>
                {method.expiry && (
                  <p className="text-gray-400 text-xs font-semibold mt-0.5">Expires {method.expiry}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!method.isDefault && (
                <button
                  onClick={() => setDefault(method.id)}
                  className="text-xs font-bold text-cl-green hover:underline cursor-pointer whitespace-nowrap"
                >
                  Set Default
                </button>
              )}
              <button
                onClick={() => removeMethod(method.id)}
                className="w-9 h-9 flex items-center justify-center rounded-xl border-2 border-transparent text-gray-400 hover:border-red-400 hover:text-red-500 transition-all cursor-pointer flex-shrink-0"
                aria-label="Remove"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Security note */}
      <div className="bg-gray-50 border-2 border-transparent rounded-2xl p-5 flex items-start gap-3">
        <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-dark flex-shrink-0">
          <i className="ri-shield-check-line text-cl-dark text-sm"></i>
        </span>
        <div>
          <p className="font-black text-cl-dark text-sm">Your payment info is secure</p>
          <p className="text-gray-500 text-xs mt-1 leading-relaxed">
            Card details are tokenised and stored securely. We never store your full card number or CVV. All transactions are protected by 256-bit SSL encryption.
          </p>
        </div>
      </div>
    </div>
  );
}
